import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Swords, UserPlus, Dumbbell, Hammer, Trophy, GitBranch,
  ShieldHalf, ArrowRight, Crown, Check, Clock, Skull, PawPrint,
} from "lucide-react";

const HERO = "/og-image.png";
const HELMET = "/gladiator-gritty.png";
const COLO = "https://images.unsplash.com/photo-1663143050642-69240b347b2b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const SHOTS = [
  { src: "/assets/screenshot1.png", alt: "Lanista gladiator manager dashboard — your ludus at a glance with your champions" },
  { src: "/assets/screenshot2.png", alt: "Recruit pool — scout new gladiators with hidden potential" },
  { src: "/assets/screenshot3.png", alt: "Weekly real-time training schedule for your gladiators" },
  { src: "/assets/screenshot4.png", alt: "Gladiator profile — loadout, combat skills and career fights" },
];

const FEATURES = [
  { icon: UserPlus, title: "Recruit from the Pool", body: "Scout a rotating pool of raw fighters across three combat classes. Hidden potential is revealed as stars the longer you train them." },
  { icon: Dumbbell, title: "Real-Time Training", body: "Schedule weekly training that runs in real time — even while you're offline. Push your gladiators through their prime before age claims them." },
  { icon: Hammer, title: "Equipment & Blacksmith", body: "Salvage fragments from the arena, forge weapons and armor with random affixes, repair durability, or melt gear for a refund." },
  { icon: PawPrint, title: "Beasts & Breeding", body: "Capture exotic beasts from across the Roman Empire — Atlas Bears, Barbary Lions, Anatolian Leopards, Eurasian Wolves, Nile Crocodiles, and Barbary Macaques. Breed them to create stronger offspring with enhanced stats that fight alongside your gladiators." },
  { icon: Swords, title: "Tactical PvP Battles", body: "Challenge other players in auto-sim duels with live colour commentary and post-match headlines." },
  { icon: GitBranch, title: "Class Skill Trees", body: "Earn skill points from ranked fights and unlock class-specific tactical nodes — all nine are yours if your gladiator fights long enough." },
  { icon: Trophy, title: "Global Rankings", body: "Climb the live rankings, build win streaks, and immortalize your champions in the public Hall of the Retired." },
];

const STEPS = [
  { n: "01", title: "Found your Ludus", body: "Create a free account and recruit your first gladiators." },
  { n: "02", title: "Train & Equip", body: "Build a weekly regimen and forge gear suited to each class." },
  { n: "03", title: "Fight & Climb", body: "Challenge rivals, win streaks, and rise up the rankings." },
];

export default function Landing() {
  const navigate = useNavigate();
  const playFree = () => navigate("/login", { state: { register: true } });
  const logIn = () => navigate("/login");

  const [topGlads, setTopGlads] = useState([]);
  useEffect(() => {
    api.get("/public/top-gladiators")
      .then((r) => setTopGlads(r.data?.top || []))
      .catch(() => setTopGlads([]));
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 font-manrope overflow-x-hidden relative">
      {/* Textured background layers */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* Noise/grain texture */}
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }} />
        
        {/* STRONG diagonal scratch marks - KEPT ENHANCED */}
        <div className="absolute inset-0 opacity-[0.25]" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 100px, rgba(200, 200, 200, 0.3) 100px, rgba(200, 200, 200, 0.3) 102px),
                           repeating-linear-gradient(-45deg, transparent, transparent 150px, rgba(200, 200, 200, 0.15) 150px, rgba(200, 200, 200, 0.15) 152px),
                           repeating-linear-gradient(60deg, transparent, transparent 80px, rgba(180, 180, 180, 0.25) 80px, rgba(180, 180, 180, 0.25) 82px),
                           repeating-linear-gradient(120deg, transparent, transparent 120px, rgba(160, 160, 160, 0.2) 120px, rgba(160, 160, 160, 0.2) 122px)`
        }} />
        
        {/* Arena stone texture */}
        <div className="absolute inset-0 opacity-[0.1]" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, rgba(0, 0, 0, 0.08) 0%, transparent 50%)`,
          backgroundSize: '350px 350px'
        }} />
      </div>
      
      <div className="relative z-10">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-zinc-950/70 border-b border-amber-900/30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Swords className="text-amber-500" size={22} />
            <span className="font-cinzel text-lg tracking-[0.25em] uppercase text-amber-300">Lanista</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="nav-login-btn" onClick={logIn}
              className="text-sm text-zinc-300 hover:text-amber-300 transition-colors px-3 py-2">Log In</button>
            <button data-testid="nav-play-btn" onClick={playFree}
              className="text-sm font-cinzel uppercase tracking-wide px-4 py-2 bg-amber-500 text-zinc-950 hover:bg-amber-400 transition-colors rounded-sm">
              Play Free
            </button>
          </div>
        </div>
      </header>
      
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Gladiator standing in a Roman arena at golden hour" className="w-full h-full object-cover object-center opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-zinc-950/60" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-24 md:py-36">
          <div className="max-w-2xl fade-up">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-amber-400/90 border border-amber-700/40 rounded-full px-3 py-1 mb-6">
              <Clock size={12} /> Real-time online gladiator manager
            </span>
            <h1 className="font-cinzel font-black uppercase leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-zinc-50">
              Become the ultimate <span className="text-amber-400">Lanista</span>
              <span className="block mt-3 text-xl sm:text-2xl lg:text-3xl font-bold text-amber-300/90 normal-case tracking-normal">
                The free online gladiator manager game
              </span>
            </h1>
            <p className="mt-6 text-lg text-zinc-300 max-w-xl">
              Lanista is a free online gladiator game where you recruit gladiators, train them in real time, forge legendary gear, capture and breed exotic beasts, and battle other players in tactical PvP arena combat. Build your ludus and carve your name into the rankings.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button data-testid="hero-play-btn" onClick={playFree}
                className="group flex items-center gap-2 font-cinzel uppercase tracking-wide px-7 py-3.5 bg-amber-500 text-zinc-950 hover:bg-amber-400 transition-colors rounded-sm">
                Play Free <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button data-testid="hero-login-btn" onClick={logIn}
                className="font-cinzel uppercase tracking-wide px-7 py-3.5 border border-amber-700/50 text-amber-200 hover:border-amber-500 hover:text-amber-100 transition-colors rounded-sm">
                Enter the Ludus
              </button>
            </div>
            <p className="mt-5 text-xs text-zinc-500">Free to play · No download · Play in your browser</p>
          </div>
        </div>
      </section>

      {/* Strip */}
      <div className="border-y border-amber-900/30 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[["3", "Combat classes"], ["∞", "Tactical depth"], ["Live", "Real-Time PvP"], ["24/7", "Training in real time"]].map(([v, l]) => (
            <div key={l}>
              <div className="font-cinzel text-2xl text-amber-400">{v}</div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <h2 className="font-cinzel text-3xl md:text-4xl uppercase text-zinc-50 tracking-wide">Everything a lanista needs</h2>
          <p className="mt-3 text-zinc-400">A deep gladiator management game built around long-term progression, strategy, and player-versus-player competition.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-amber-900/20 border border-amber-900/20">
          {FEATURES.map((f) => (
            <div key={f.title} data-testid={`feature-${f.title}`} className="bg-zinc-950 p-7 hover:bg-zinc-900/60 transition-colors">
              <f.icon className="text-amber-500 mb-4" size={26} />
              <h3 className="font-cinzel text-lg text-amber-100 tracking-wide mb-2">{f.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Showcase */}
      <section className="bg-zinc-900/30 border-y border-amber-900/30">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={HELMET} alt="Battle-scarred Roman gladiator in shadow" className="w-full h-80 object-cover object-top rounded-sm border border-amber-900/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/60 to-transparent rounded-sm" />
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-[0.25em] text-amber-400/90">Choose your class</span>
            <h2 className="font-cinzel text-3xl uppercase text-zinc-50 tracking-wide mt-3 mb-4">Dimachaerus · Retiarius · Provocator</h2>
            <p className="text-zinc-400 leading-relaxed mb-5">
              Each class fights with a distinct style and gear loadout — dual blades, net and trident, or sword and shield. Master class-specific skill trees and equip the right weapons to dominate the arena.
            </p>
            <ul className="space-y-2">
              {["Hidden potential revealed through scouting", "Durability, repair and affix systems", "Age, prime and retirement lifecycle"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-zinc-300">
                  <Check size={15} className="text-amber-400 mt-0.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-6xl mx-auto px-6 py-20 md:py-28">
        <h2 className="font-cinzel text-3xl md:text-4xl uppercase text-zinc-50 tracking-wide mb-12">How it works</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {STEPS.map((s) => (
            <div key={s.n} className="border-l-2 border-amber-700/50 pl-5">
              <div className="font-cinzel text-amber-500/60 text-3xl mb-2">{s.n}</div>
              <h3 className="font-cinzel text-xl text-amber-100 tracking-wide mb-2">{s.title}</h3>
              <p className="text-sm text-zinc-400">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VIP teaser */}
      <section className="relative overflow-hidden">
        <img src={COLO} alt="Roman colosseum arena" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/70" />
        <div className="relative max-w-6xl mx-auto px-6 py-20 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-2 text-amber-300 font-cinzel uppercase tracking-widest text-sm mb-3">
              <Crown size={16} /> Optional VIP
            </span>
            <h2 className="font-cinzel text-3xl uppercase text-zinc-50 tracking-wide">Go further as a VIP Lanista</h2>
            <p className="mt-3 text-zinc-400">Gain +5 gladiator slots, upload a custom profile avatar, scout twice as fast, and fight twice as often — from just $1.99 your first month, then $4.49/mo. Entirely optional; the core game is free.</p>
          </div>
          <button data-testid="vip-cta-btn" onClick={playFree}
            className="shrink-0 font-cinzel uppercase tracking-wide px-7 py-3.5 bg-amber-500 text-zinc-950 hover:bg-amber-400 transition-colors rounded-sm">
            Start Free
          </button>
        </div>
      </section>

      {/* Screenshots gallery */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <span className="text-[11px] uppercase tracking-[0.25em] text-amber-400/90">A look inside the arena</span>
          <h2 className="font-cinzel text-3xl md:text-4xl uppercase text-zinc-50 tracking-wide mt-3">See the game in action</h2>
          <p className="mt-3 text-zinc-400 max-w-2xl mx-auto">From managing your ludus to scouting recruits, training fighters and battling rivals — here&apos;s what awaits.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SHOTS.map((s, i) => (
            <figure key={i} data-testid={`landing-screenshot-${i + 1}`}
              className="overflow-hidden border border-amber-900/30 rounded-sm bg-zinc-900/40 hover:border-amber-700/50 transition-colors">
              <img src={s.src} alt={s.alt} loading="lazy" className="w-full h-auto block" />
            </figure>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 py-24 text-center">
        <Skull className="mx-auto text-amber-500/70 mb-5" size={32} />
        <h2 className="font-cinzel text-3xl md:text-5xl uppercase text-zinc-50 tracking-wide leading-tight">The arena is waiting</h2>
        <p className="mt-4 text-zinc-400 max-w-xl mx-auto">Join Lanista - Gladiator Manager and build a stable of champions today. Glory is earned in blood and strategy.</p>
        <button data-testid="footer-play-btn" onClick={playFree}
          className="mt-8 inline-flex items-center gap-2 font-cinzel uppercase tracking-wide px-9 py-4 bg-amber-500 text-zinc-950 hover:bg-amber-400 transition-colors rounded-sm">
          Play Free <ArrowRight size={18} />
        </button>
      </section>

      {/* Current Rankings — top 10 gladiators by rating (public) */}
      {topGlads.length > 0 && (
        <section data-testid="landing-rankings" className="max-w-3xl mx-auto px-6 pb-24">
          <div className="text-center mb-8">
            <Trophy className="mx-auto text-amber-500/80 mb-3" size={28} />
            <h2 className="font-cinzel text-2xl md:text-4xl uppercase text-zinc-50 tracking-wide">Current Rankings</h2>
            <p className="mt-3 text-sm text-zinc-500">The ten highest-rated gladiators fighting in the arena right now.</p>
          </div>
          <div className="border border-amber-900/30 rounded-sm overflow-hidden bg-zinc-900/40">
            <div className="hidden sm:grid grid-cols-[3rem_1fr_8rem_5rem_5rem] gap-2 px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-amber-900/20">
              <span>Rank</span><span>Gladiator</span><span>Lanista</span><span className="text-right">Record</span><span className="text-right">Rating</span>
            </div>
            {topGlads.map((g) => (
              <div key={g.rank} data-testid={`rank-row-${g.rank}`}
                className="grid grid-cols-[3rem_1fr_auto] sm:grid-cols-[3rem_1fr_8rem_5rem_5rem] gap-2 px-4 py-3 items-center border-b border-amber-900/10 last:border-0 hover:bg-amber-500/[0.03] transition-colors">
                <span className={`font-cinzel text-lg ${g.rank === 1 ? "text-amber-400" : g.rank <= 3 ? "text-amber-500/80" : "text-zinc-500"}`}>
                  {g.rank === 1 ? <Crown size={18} className="inline text-amber-400" /> : g.rank}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-zinc-100 font-medium">{g.name}</span>
                  <span className="block text-[11px] text-zinc-500">{g.cls}</span>
                </span>
                <span className="hidden sm:block truncate text-sm text-zinc-400">{g.owner_name}</span>
                <span className="hidden sm:block text-right text-sm text-zinc-400">{g.wins}–{g.losses}</span>
                <span className="text-right font-cinzel text-amber-300">{g.rating}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <button data-testid="rankings-play-btn" onClick={playFree}
              className="inline-flex items-center gap-2 font-cinzel uppercase tracking-wide text-sm px-7 py-3 border border-amber-500/60 text-amber-300 hover:bg-amber-500 hover:text-zinc-950 transition-colors rounded-sm">
              Claim your spot <ArrowRight size={16} />
            </button>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-amber-900/30 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Swords className="text-amber-500" size={18} />
            <span className="font-cinzel text-sm tracking-[0.25em] uppercase text-amber-300">Lanista — Gladiator Manager</span>
          </div>
          <p className="text-xs text-zinc-600 text-center md:text-right max-w-md">
            A free online gladiator management and lanista simulation game with real-time training and tactical PvP arena battles.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <button onClick={() => navigate("/about")} className="text-zinc-400 hover:text-amber-300 transition-colors">About</button>
            <button onClick={() => navigate("/updates")} className="text-zinc-400 hover:text-amber-300 transition-colors">Updates</button>
            <button onClick={() => navigate("/faq")} className="text-zinc-400 hover:text-amber-300 transition-colors">FAQ</button>
            <button onClick={() => navigate("/guide")} className="text-zinc-400 hover:text-amber-300 transition-colors">Guide</button>
            <button onClick={() => navigate("/strategy")} className="text-zinc-400 hover:text-amber-300 transition-colors">Strategy</button>
            <button onClick={() => navigate("/hall")} className="text-zinc-400 hover:text-amber-300 transition-colors">Hall of Fame</button>
            <button onClick={logIn} className="text-zinc-400 hover:text-amber-300 transition-colors">Log In</button>
            <button onClick={playFree} className="text-amber-400 hover:text-amber-300 transition-colors">Play Free</button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-4 -mt-2 text-center md:text-right flex items-center justify-center md:justify-end gap-4">
          <button data-testid="privacy-policy-link" onClick={() => navigate("/privacy-policy")}
            className="text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors underline underline-offset-2">
            Privacy Policy
          </button>
          <button data-testid="contact-link" onClick={() => navigate("/contact")}
            className="text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors underline underline-offset-2">
            Contact Us
          </button>
          <button data-testid="terms-link" onClick={() => navigate("/terms-of-service")}
            className="text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors underline underline-offset-2">
            Terms of Service
          </button>
        </div>
      </footer>
      </div>
    </div>
  );
}
